import { Group, LoadingOverlay, Modal, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useApi } from 'api/api-context';
import BrainItMoreInfo from 'components/BrainItMoreInfo';
import Button from 'components/Button';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { DateInput } from 'components/inputs/DateInput';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import KepMaintenanceNotice from 'components/modals/kep-maintenance/KepMaintenanceNotice';
import P1Regular from 'components/typography/P1Regular';
import { addDays } from 'date-fns';
import { FEATURE_TOGGLE_KEP_MAINTENANCE } from 'env';
import panic from 'errors/panic';
import useLoadingAction from 'hooks/use-loading-action';
import { useCallback, useEffect, useState } from 'react';
import { RevisionListResponse } from 'api/actions/revision-list/revision-list-response';
import RevisionList from 'components/RevisionList';
import Toast from 'components/Toast';

/**
 * Parameters of the SignRevisionModal component.
 */
export interface SignRevisionModalProps {
  revisionIds: number[];
  message: string | JSX.Element;
  opened: boolean;
  onClose: () => void;
  onSign: (({ deadline }: { deadline: string }) => void) | (() => Promise<void>);
}

interface FormValues {
  deadline: string;
}

/**
 * Modal sign revision and set document signature validity.
 */
export default function SignRevisionModal({ revisionIds, message, opened, onClose, onSign }: SignRevisionModalProps) {
  const signAndClose = useCallback(
    async ({ deadline }: { deadline: string }) => {
      await onSign({ deadline });
      onClose();
    },
    [onSign, onClose]
  );

  const { getAction } = useApi();
  const [{ loading }, onSignImpl] = useLoadingAction(signAndClose);
  const [inKepHashArchive, setInKepHashArchive] = useState(true);
  const [organizationName, setOrganizationName] = useState('');
  const [fetching, setFetching] = useState(true);
  const [revisions, setRevisions] = useState<RevisionListResponse>([]);
  const [revisionKind, setRevisionKind] = useState<'revision' | 'form'>('revision');
  const isKepMaintenanceMode = FEATURE_TOGGLE_KEP_MAINTENANCE;
  const isBusy = !isKepMaintenanceMode && (loading || fetching);

  const form = useForm<FormValues>({
    initialValues: {
      deadline: '',
    },
    validate: {
      deadline: createValidator([required]),
    },
  });

  useEffect(() => {
    if (opened) {
      const revisionGetListAction = getAction('RevisionList');

      revisionGetListAction({ query: { filters: { 'revisionId.in': revisionIds } } })
        .then((fetchedRevisions) => {
          setRevisions(fetchedRevisions);
          setRevisionKind(fetchedRevisions[0]?.kind === 'revision' ? 'revision' : 'form');

          // Check if all revisions have the same organization
          const organizations = new Set(fetchedRevisions.map(({ organization: { organizationId } }) => organizationId));

          if (organizations.size > 1) {
            panic(new Error('All revisions must belong to the same organization'));
            onClose();
            return;
          }

          const firstOrg = fetchedRevisions[0].organization;
          setOrganizationName(firstOrg.organizationName);

          if (!firstOrg.inKepHashArchive) {
            const [tomorrow] = addDays(new Date(), 1).toISOString().split('T');
            form.setFieldValue('deadline', tomorrow);
            setInKepHashArchive(false);
          } else {
            // Compute the maximum nextRevisionDate across all revisions
            const nextRevisionDates = [
              ...fetchedRevisions.map(({ nextRevisionDate }) => nextRevisionDate),
              ...fetchedRevisions.map(({ documentValidUntil }) => documentValidUntil),
            ].filter((date): date is string => !!date && /\d{4}-\d{2}-\d{2}/.test(date));

            if (nextRevisionDates.length > 0) {
              // Find the furthest date into the future
              const maxDate = nextRevisionDates.reduce((max, current) =>
                new Date(current) > new Date(max) ? current : max
              );
              form.setFieldValue('deadline', maxDate);
            } else {
              form.setFieldValue('deadline', '');
            }
          }
        })
        .catch(panic)
        .finally(() => setFetching(false));
    }
  }, [opened, revisionIds]);

  return (
    <Modal
      centered
      onClose={onClose}
      opened={opened}
      title={revisionKind === 'revision' ? 'Podpísať revíznu správu' : 'Podpísať formulár'}
      zIndex={1001}
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      closeButtonProps={{ loading: isBusy }}
    >
      <LoadingOverlay visible={isBusy} loaderProps={{ size: 'md' }} zIndex={1000} />
      {isKepMaintenanceMode ? (
        <Stack spacing={24}>
          <KepMaintenanceNotice />
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(onSignImpl)}>
          <Stack spacing={24}>
            <Stack spacing={16}>
              <P1Regular>{message}</P1Regular>
              <RevisionList revisions={revisions} />
              <DateInput
                size="md"
                label={
                  <Group noWrap spacing={4}>
                    <span>Deadline na prepečiatkovanie</span>
                    <QuestionMarkTooltip
                      multiline
                      width={300}
                      label={
                        revisionKind === 'revision'
                          ? 'Deadline na prepečiatkovanie je dátum, do kedy má byť dokument uložený v KEP Hash archíve. Typicky je to dátum ďalšej revízie.'
                          : 'Deadline na prepečiatkovanie je dátum, do kedy má byť dokument uložený v KEP Hash archíve. Typicky je to dátum ďalšieho vypracovania.'
                      }
                    />
                  </Group>
                }
                placeholder="Zadajte dátum"
                popoverPosition="bottom-start"
                readOnly={loading || !inKepHashArchive}
                {...form.getInputProps('deadline')}
              />
              {!inKepHashArchive && (
                <Toast
                  type="info"
                  message={`Organizácia ${organizationName} nepoužíva KEP Hash archív, deadline bude nastavený na zajtrajší dátum.`}
                  withCloseButton={false}
                />
              )}
            </Stack>
            {!loading && (
              <Group position="apart">
                <BrainItMoreInfo />
                <Group position="right">
                  <Button variant="subtle-gray" onClick={onClose}>
                    Zrušiť
                  </Button>
                  <Button variant="primary" type="submit" data-autofocus>
                    Vyžiadať podpis
                  </Button>
                </Group>
              </Group>
            )}
          </Stack>
        </form>
      )}
    </Modal>
  );
}
